/* ============================================================================
   LIG — Custom Orders Gallery (JS)
   ----------------------------------------------------------------------------
   Restores the legacy WooCommerce "personal product gallery" links:
       /pages/custom-orders?order_id=XXXXX
   (old shape /custom-orders/?order_id=XXXXX is preserved via a URL redirect)

   How it works
   - Reads `order_id` from the URL query string.
   - Live-scans the public catalog via /products.json (paginated), matching any
     product whose body_html contains the migrated order code. Matrixify's
     WooCommerce import left the codes in the description, but Shopify's HTML
     sanitizer escaped the angle brackets, so the stored form is `&lt;XXXXX&gt;`
     (renders as visible `<XXXXX>` text). We match both the escaped and the raw
     bracket forms to be safe.
   - Renders matches progressively into an on-brand product-card grid.
   - Stale-while-revalidate cache (sessionStorage, 10 min): a returning visitor
     sees their items instantly, then a background re-scan picks up any items
     added to that order since the cache was written.

   No Storefront/Admin API and no tokens: storefront search can't find these
   codes (its indexer strips `<digits>` as an HTML tag), so /products.json —
   which exposes raw body_html — is the only reliable source.
   ============================================================================ */
(function () {
  'use strict';

  var CACHE_PREFIX = 'lig-co:';
  var CACHE_TTL_MS = 10 * 60 * 1000;   // 10 minutes
  var PAGE_SIZE = 250;                 // Shopify products.json max
  var BATCH = 3;                       // pages fetched in parallel per wave (gentle on the JSON rate limit)
  var MAX_PAGES = 60;                  // hard ceiling (15k products) — safety
  var MAX_RETRIES = 4;                 // per-page retries on 429 / transient error
  var RETRY_BASE_MS = 600;             // backoff base (exponential)

  var root = document.querySelector('[data-lig-custom-orders]');
  if (!root) return;

  var elGrid = root.querySelector('[data-co-grid]');
  var elStatus = root.querySelector('[data-co-status]');
  var elGreeting = root.querySelector('[data-co-greeting]');
  var elCount = root.querySelector('[data-co-count]');

  // ---- URL params -----------------------------------------------------------
  var params = new URLSearchParams(window.location.search);
  var orderId = (params.get('order_id') || params.get('order') || '').trim();
  var customerName = (params.get('name') || '').trim();

  if (customerName && elGreeting) {
    // optional &name= passthrough for future emails; sanitized as text only
    elGreeting.textContent = 'Hi ' + customerName + ' —';
    elGreeting.hidden = false;
  }

  if (!orderId || !/^\d{2,12}$/.test(orderId)) {
    setStatus('Add your order number to the link to see your custom items. ' +
              'If you reached this page from an email and still see this, ' +
              'reply to that email and we’ll resend your gallery link.');
    return;
  }

  // ---- Matching needles -----------------------------------------------------
  // Stored form is `&lt;ID&gt;`; also accept a raw `<ID>` in case any product
  // was edited and re-saved with literal brackets. Delimiters guard against an
  // ID being a substring of a longer number (e.g. 66591 inside 166591).
  var needles = ['&lt;' + orderId + '&gt;', '<' + orderId + '>'];
  function matches(bodyHtml) {
    if (!bodyHtml) return false;
    for (var i = 0; i < needles.length; i++) {
      if (bodyHtml.indexOf(needles[i]) !== -1) return true;
    }
    return false;
  }

  // ---- Cache (stale-while-revalidate) --------------------------------------
  var cacheKey = CACHE_PREFIX + orderId;
  var rendered = Object.create(null); // handle -> true (dedupe across paint/revalidate)

  var cached = readCache();
  if (cached && cached.items && cached.items.length) {
    paint(cached.items);
    setStatus('');
  } else {
    setStatus('Gathering your custom pieces…');
  }

  scanCatalog().then(function (result) {
    var items = result.items;
    // Re-render from the authoritative live scan (covers newly added items and
    // items that may have been removed since the cache was written).
    if (items.length) {
      paint(items);
      setStatus('');
      // Only cache a scan we know is complete — never persist a partial result
      // produced when one or more catalog pages failed after all retries.
      if (result.complete) writeCache(items);
    } else if (!cached) {
      if (result.complete) {
        setStatus('We couldn’t find custom items for order #' + escapeText(orderId) +
                  '. Double-check the link, or reply to your email and we’ll help.');
      } else {
        setStatus('We’re having trouble reaching the catalog right now. ' +
                  'Please refresh in a moment, or reply to your email and we’ll help.');
      }
    }
    updateCount();
  }).catch(function (err) {
    if (!cached) {
      setStatus('Something went wrong loading your gallery. Please refresh, ' +
                'or reply to your email and we’ll send it along.');
    }
    if (window.console && console.warn) console.warn('[lig-custom-orders]', err);
  });

  // ---- Catalog scan ---------------------------------------------------------
  function scanCatalog() {
    var found = [];
    var page = 1;
    var hadError = false; // true if any page exhausted its retries — don't cache a partial result

    function delay(ms) {
      return new Promise(function (res) { setTimeout(res, ms); });
    }

    // Fetch one catalog page, retrying on 429 / transient failure with
    // exponential backoff. Returns { products: [...] } or null on hard failure
    // (so a throttled page is NOT silently treated as "no products").
    function fetchPage(p, attempt) {
      attempt = attempt || 0;
      var url = '/products.json?limit=' + PAGE_SIZE + '&page=' + p;
      return fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
        .then(function (r) {
          if (r.status === 429 || r.status >= 500) throw new Error('retry:' + r.status);
          if (!r.ok) return { products: [] };
          return r.json();
        })
        .then(function (d) { return { products: (d && d.products) || [] }; })
        .catch(function () {
          if (attempt < MAX_RETRIES) {
            return delay(RETRY_BASE_MS * Math.pow(2, attempt))
              .then(function () { return fetchPage(p, attempt + 1); });
          }
          hadError = true;
          return null; // hard failure after retries
        });
    }

    function wave() {
      var batch = [];
      for (var i = 0; i < BATCH && page <= MAX_PAGES; i++, page++) {
        batch.push(fetchPage(page));
      }
      if (!batch.length) return Promise.resolve({ items: found, complete: !hadError });

      return Promise.all(batch).then(function (results) {
        var sawFull = false;
        results.forEach(function (res) {
          if (!res) return;                         // hard-failed page (already flagged hadError)
          var prods = res.products;
          if (prods.length === PAGE_SIZE) sawFull = true;
          for (var j = 0; j < prods.length; j++) {
            if (matches(prods[j].body_html)) {
              found.push(toCard(prods[j]));
            }
          }
        });
        // Progressive paint after each wave so items appear as we go.
        if (found.length) { paint(found); setStatus(''); updateCount(); }
        // Stop once a wave's last page wasn't full (end of catalog).
        return sawFull ? wave() : { items: found, complete: !hadError };
      });
    }

    return wave();
  }

  // ---- Card model -----------------------------------------------------------
  function toCard(p) {
    var img = (p.images && p.images[0] && p.images[0].src) || '';
    var prices = (p.variants || []).map(function (v) { return parseFloat(v.price); })
      .filter(function (n) { return !isNaN(n); });
    var min = prices.length ? Math.min.apply(null, prices) : null;
    var max = prices.length ? Math.max.apply(null, prices) : null;
    var available = (p.variants || []).some(function (v) { return v.available; });
    return {
      handle: p.handle,
      title: p.title,
      image: img,
      minPrice: min,
      multiPrice: (min !== null && max !== null && max > min),
      available: available
    };
  }

  // ---- Rendering ------------------------------------------------------------
  function paint(items) {
    if (!elGrid) return;
    var frag = document.createDocumentFragment();
    var added = 0;
    items.forEach(function (it) {
      if (!it.handle || rendered[it.handle]) return;
      rendered[it.handle] = true;
      frag.appendChild(cardEl(it));
      added++;
    });
    if (added) {
      elGrid.appendChild(frag);
      root.setAttribute('data-co-has-items', 'true');
    }
    updateCount();
  }

  function cardEl(it) {
    var a = document.createElement('a');
    // NOTE: do NOT use the global `lig-reveal` class here. That class is set to
    // opacity:0 by lig-interactions.js (via `lig-reveal-ready` on <body>) and is
    // only un-hidden by an IntersectionObserver that runs once at page load — it
    // never sees these dynamically injected cards, so they'd stay invisible.
    // `.lig-co-card` carries its own self-contained fade-in instead.
    a.className = 'lig-co-card';
    a.href = '/products/' + it.handle;

    var imgWrap = document.createElement('div');
    imgWrap.className = 'lig-co-card__img';
    if (it.image) {
      var im = document.createElement('img');
      im.src = sizedImage(it.image, 800);
      im.loading = 'lazy';
      im.alt = it.title || '';
      imgWrap.appendChild(im);
    }
    if (!it.available) {
      var so = document.createElement('span');
      so.className = 'lig-co-card__soldout';
      so.textContent = 'Archived';
      imgWrap.appendChild(so);
    }
    a.appendChild(imgWrap);

    var meta = document.createElement('div');
    meta.className = 'lig-co-card__meta';
    var name = document.createElement('span');
    name.className = 'lig-co-card__name';
    name.textContent = it.title || '';
    meta.appendChild(name);

    if (it.minPrice !== null) {
      var price = document.createElement('span');
      price.className = 'lig-co-card__price';
      price.textContent = (it.multiPrice ? 'From ' : '') + money(it.minPrice);
      meta.appendChild(price);
    }
    a.appendChild(meta);
    return a;
  }

  function sizedImage(src, w) {
    // Insert a Shopify CDN width transform if not already present.
    if (!src || src.indexOf('cdn.shopify') === -1) return src;
    if (src.indexOf('width=') !== -1) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function money(n) {
    // Store currency is USD; format to $X,XXX.XX (whole-dollar drops cents).
    var hasCents = Math.round(n * 100) % 100 !== 0;
    return '$' + n.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2
    });
  }

  // ---- Status / count -------------------------------------------------------
  function setStatus(msg) {
    if (!elStatus) return;
    elStatus.textContent = msg || '';
    elStatus.hidden = !msg;
  }

  function updateCount() {
    if (!elCount) return;
    var n = Object.keys(rendered).length;
    if (!n) { elCount.hidden = true; return; }
    elCount.hidden = false;
    elCount.textContent = n + (n === 1 ? ' piece' : ' pieces');
  }

  // ---- Cache helpers --------------------------------------------------------
  function readCache() {
    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || !obj.t || (now() - obj.t) > CACHE_TTL_MS) return null;
      return obj;
    } catch (_) { return null; }
  }
  function writeCache(items) {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ t: now(), items: items }));
    } catch (_) {}
  }
  function now() { return new Date().getTime(); }

  function escapeText(s) { return String(s).replace(/[<>&"']/g, ''); }
})();
